import { useStyles } from '@archipro-design/aria';
import {
    COLUMN_WIDTH_100,
    DUO_COLUMN_WIDTH,
} from '@modules/page-builder/config/column-constants';
import type { ImageColumnProps } from '@modules/page-builder/types';
import DetailSingleImage from '../detail-single-image-shell/DetailSingleImageShell';
import { SingleImageContainer } from '../../common/pagebuilder.style';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import ImageWithPaintPicker from '../../common/image-with-paint-picker/ImageWithPaintPicker';
import { ImageModuleExtraSpacing } from '../detail-modules/DetailModules.style';
import { useRef } from 'react';
import { useInViewport } from 'ahooks';
import { cn } from '@rocco/utils/cn';

interface SingleImageModuleProps {
    column: ImageColumnProps;
    isProject?: boolean;
}

const SingleImageModule = ({ column, isProject }: SingleImageModuleProps) => {
    const elementRef = useRef<HTMLDivElement>(null);
    const [inViewport] = useInViewport(elementRef, {
        threshold: 0.4,
    });

    const { css } = useStyles({
        fullScreen: column.fullScreen,
        inViewport: inViewport,
    });

    if (!column.imageUrl) {
        return null;
    }
    // If the image is smaller than the 1440px width from duo column,
    // then its a medium image instead of a full width one.
    const isMedium = column.dimension.width < DUO_COLUMN_WIDTH;

    return (
        <div
            ref={elementRef}
            className={css(SingleImageContainer, ImageModuleExtraSpacing)}
        >
            <DetailSingleImage
                caption={column.caption}
                libraryImage={column?.libraryImage}
                tags={column.tags}
                uniqueApprovedTags={column.uniqueApprovedTags}
                imageURL={column.imageUrl}
                dimension={column.dimension}
                moduleType={
                    isMedium ? 'MediumSingleImageModule' : 'SingleImageModule'
                }
                isProject={isProject}
                fullScreen={column.fullScreen}
                inViewPort={inViewport}
            >
                <ImageWithPaintPicker
                    src={column.imageUrl}
                    width={column.dimension.width}
                    height={column.dimension.height}
                    layout={'fill'}
                    objectFit={'cover'}
                    sizes={getImagesSizes(COLUMN_WIDTH_100)}
                    alt={column.caption || column?.libraryImage?.Title || ''}
                    className={cn(
                        'overflow-hidden transition-all duration-500 ease-in-out',
                        column.fullScreen && inViewport
                            ? 'rounded-none'
                            : 'md:rounded-2'
                    )}
                />
            </DetailSingleImage>
        </div>
    );
};

export default SingleImageModule;
